import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Option } from 'common/components/select/select.component';
import { DateRange } from 'private/app/models/account-admin-program.model';
import { ProductLiteratureDocument } from 'common/models/document';
import { FileIcons } from 'common/models/fileIcons';
import { format, parse, startOfDay, startOfWeek, subDays } from 'date-fns';

export interface WhatsNewResultRow {
    id: string;
    title: string;
    type: string;
    catalogNumber: string;
    brands: string;
    releaseDate: string;
    documentUrl: string;
    fileType: string;
    fileIcon: string;
}

@Injectable({ providedIn: 'root' })
export class WhatsNewResultsHelperService {
    public static readonly SELECT_DATE_RANGE_VALUE = 'SELEECT_A_DATE_RANGE';
    public static readonly THIS_WEEK_VALUE = 'THIS_WEEK';

    public static readonly DATE_OPTION_WEEK_COUNT: Record<string, number> = {
        LAST_WEEK: 1,
        TWO_WEEKS_AGO: 2,
        THREE_WEEKS_AGO: 3,
        FOUR_WEEKS_AGO: 4,
        FIVE_WEEKS_AGO: 5,
        SIX_WEEKS_AGO: 6,
        SEVEN_WEEKS_AGO: 7
    };

    public static WhatsNewResultsForm = new UntypedFormGroup({
        brand: new UntypedFormControl('', [Validators.required]),
        documentType: new UntypedFormControl('', [Validators.required]),
        date: new UntypedFormControl('', [Validators.required]),
        fromDate: new UntypedFormControl('', [Validators.required]),
        toDate: new UntypedFormControl('', [Validators.required])
    });

    public documentTypeOptions: Option[];


    constructor(
        private translateService: TranslateService
    ) { }


    public getDropDownOptions(): Option[] {
        const documentTypeOptionsObj = this.translateService.instant('WHATS_NEW_RESULTS.DOCUMENT_TYPE_OPTIONS');

        return Object.keys(documentTypeOptionsObj).map((key) => ({
            name: documentTypeOptionsObj[key],
            value: documentTypeOptionsObj[key]
        }));
    }

    public getDateOptions(): Option[] {
        const dateOptionsObj = this.translateService.instant('WHATS_NEW_RESULTS.DATE_OPTIONS');

        return Object.keys(dateOptionsObj).map((key) => ({
            name: dateOptionsObj[key],
            value: key
        }));
    }

    public getPresetDateRange(dateOptionValue: string, today: number): DateRange | null {
        if (dateOptionValue === WhatsNewResultsHelperService.THIS_WEEK_VALUE) {
            return {
                startDate: startOfDay(startOfWeek(today, { weekStartsOn: 0 })).getTime(),
                endDate: today
            };
        }

        const weeks = WhatsNewResultsHelperService.DATE_OPTION_WEEK_COUNT[dateOptionValue];

        if (!weeks) {
            return null;
        }

        return {
            startDate: startOfDay(subDays(today, weeks * 7)).getTime(),
            endDate: today
        };
    }

    public formatDateForPayload(dateValue: number | string | Date | null | undefined): string {
        if (!dateValue && dateValue !== 0) {
            return '';
        }

        return format(new Date(dateValue), 'MM/dd/yyyy');
    }

    public mapDocumentsToResults(documents: ProductLiteratureDocument[] = []): WhatsNewResultRow[] {
        return documents.map((document) => {
            const fileType = document.FileType || document.DocumentMIMEType || '';

            return {
                id: document.ID,
                title: document.DocumentTitle || '',
                type: (document.Subtype || []).filter(Boolean).join(', '),
                catalogNumber: document.CatalogueNumber || '',
                brands: (document.Brands || []).filter(Boolean).join(', '),
                releaseDate: document.ReleaseDate || '',
                documentUrl: this.buildDocumentUrl(document.DocumentBaseURL, document.DocumentPath),
                fileType,
                fileIcon: FileIcons[fileType] || FileIcons.Default
            };
        }).sort((left, right) => this.releaseDateTimestamp(right.releaseDate) - this.releaseDateTimestamp(left.releaseDate));
    }

    public releaseDateTimestamp(releaseDate: string): number {
        if (!releaseDate) {
            return 0;
        }

        const parsed = parse(releaseDate, 'MM/dd/yyyy', new Date());
        const timestamp = parsed.getTime();

        return Number.isNaN(timestamp) ? 0 : timestamp;
    }

    public fileIcon(fileType: string): string {
        return FileIcons[fileType] || FileIcons.Default;
    }

    private buildDocumentUrl(baseUrl: string = '', path: string = ''): string {
        if (!baseUrl && !path) {
            return '';
        }

        return `${baseUrl.replace(/\/$/, '')}${path}`;
    }
}
